// ABOUTME: The breathing scene: a sky that clears from storm to sun over a round, rain, nine clouds, rolling sea bands and the swelling canoe.
// ABOUTME: Drawn in the Reset reference's 850 x 370 px stage, scaled as one piece and centered on its host (where the ring is), so every offset and ramp is the reference's (docs/plans/crossing/evidence/breath-reset-reference).

import type { RefObject } from 'react';
import { Box, alpha } from '@mui/material';
import { keyframes, type Keyframes } from '@emotion/react';
import { WAVE_BACK_URL, WAVE_FRONT_URL } from '../../../theme/waveAssets';
import {
  CROSSING_BREATH_SKY_TOP, CROSSING_BREATH_SKY_MID, CROSSING_BREATH_SKY_BOTTOM,
  CROSSING_STORM_SKY_TOP, CROSSING_STORM_SKY_BOTTOM, CROSSING_OVERCAST_SKY_TOP, CROSSING_OVERCAST_SKY_BOTTOM,
  CROSSING_CLOUD_INK_DARK, CROSSING_CLOUD_INK_LIGHT, CROSSING_BREATH_STAGE_UPPER, CROSSING_BREATH_STAGE_LOWER,
  CROSSING_RAIN_STREAK, CROSSING_BREATH_SUN, CROSSING_SEA_MID, CROSSING_SEA_DEEP,
  CROSSING_SUN_SCENE_GRADIENT_TOP, CROSSING_SUN_SCENE_GRADIENT_MID, CROSSING_SUN_SCENE_GRADIENT_BOTTOM,
  CROSSING_SUN_SCENE_SUN, CROSSING_SUN_SCENE_SUN_HALO, CROSSING_SUN_SCENE_SUN_GLOW,
  CROSSING_SUN_SCENE_CLOUD_FILL, CROSSING_SUN_SCENE_CLOUD_FILL_SOFT,
  CROSSING_STORM_SKY_STOPS, CROSSING_STORM_SKY_GLOW, CROSSING_STORM_CLOUD_FILL, CROSSING_STORM_VEIL_DROP,
} from '../../../theme/crossingTokens';
import { RUNNER_PLATE_CLEARANCE_PX } from '../runner/plateClearance';
import { breathAmp, canoeRideHeight } from './breathMath';
import { breathSkyAt } from './breathSky';
import { useBreathStageScale } from './useBreathStageScale';
import WaaCanoe from './WaaCanoe';

// The reference's design stage, in its own px: every percentage and pixel size below is of this stage, not of the screen.
const STAGE_W = 850;
const STAGE_H = 370;
const SKY_OVERSHOOT = 90; // the sky and the rain start this far above the stage
const SKY_H = STAGE_H / 2 + SKY_OVERSHOOT; // the sky ends at the stage's 50%, the horizon
// The opening layers reach this far above the sky's top so they fill behind the header; at any stage scale of 0.75 or more,
// 800 stage px reaches the scene root's top, which is 600 screen px above the host.
const SKY_ABOVE_STAGE = 800;
// The storm's gradient is laid out over a box this much wider than the stage on each side, as the prototype's is; the layer itself
// runs as far as the base sky (REACH), so a window of any width is covered and the gradient's ends simply continue as flat colour.
const STORM_GRADIENT_SIDE_BLEED = 240;
// How far the backdrops (sky, sea) run past the stage so any screen is filled; the weather stays in the stage.
const REACH = 2500;
// How far above the host the scene's box reaches, so the sky fills behind the header and the strip.
const ABOVE_HOST_PX = 600;
// The wave bands are wider than any screen, so a wide viewport shows no gap at the water edge. The width is chosen so
// WAVE_BAND_W / 2 is 100 mod 200 and 0 mod 300, the same tile phase as the reference's 1800 px / -900 px bands.
const WAVE_BAND_W = 6600;

// The horizon and the deep-water line, as fractions of the stage.
const HORIZON_AT = '58%';
const DEEP_AT = '72%';
// Ready for compass: the scene zooms to the deck over 1.6 s and fades over 0.7 s from 0.8 s.
const ZOOM_SCALE = 4.2;
const ZOOM_TRANSITION = 'transform 1.6s cubic-bezier(.55,0,.8,.45), opacity 0.7s ease 0.8s';
const SWELL_SEC = 3;
const FRONT_BAND_LOOP_SEC = 5;
const FRONT_BAND_DELAY_SEC = -1.1;

// The canoe's bob: up and down once per cycle.
const swellCanoe = keyframes`
  0%, 100% { transform: translateY(-20px) rotate(1.4deg); }
  50% { transform: translateY(20px) rotate(-1.4deg); }
`;
const rollBack = keyframes`
  from { background-position-x: 150px; }
  to { background-position-x: -150px; }
`;
const rollFront = keyframes`
  from { background-position-x: 0px; }
  to { background-position-x: -200px; }
`;
const pvStormSlide = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;
const pvStormSettle = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;
const pvRainVeil = keyframes`
  from { background-position: 0 0; }
  to { background-position: 0 34px; }
`;
const pnRain1 = keyframes`
  from { background-position: 0 0; }
  to { background-position: 0 40px; }
`;
const pnRain2 = keyframes`
  from { background-position: 9px 13px; }
  to { background-position: 9px 62px; }
`;
const pnRain3 = keyframes`
  from { background-position: 18px 27px; }
  to { background-position: 18px 88px; }
`;

// The rain's three tiled drop fields: tile size, ellipse, alpha, loop time and keyframes. Drops fall down-left under the skew.
const RAIN_LAYERS = [
  { layer: 1, tile: '17px 40px', ellipse: '1.3px 7px', alpha: 0.75, seconds: 1.3, frames: pnRain1 },
  { layer: 2, tile: '24px 49px', ellipse: '1.2px 6px', alpha: 0.55, seconds: 1.8, frames: pnRain2 },
  { layer: 3, tile: '31px 61px', ellipse: '1.1px 8px', alpha: 0.6, seconds: 2.2, frames: pnRain3 },
];

interface Cloud { left: number; top: number; width: number; height: number; group: 1 | 2 | 3 }
const CLOUDS: Cloud[] = [
  { left: 38, top: 15, width: 120, height: 51, group: 2 }, { left: 27, top: 8, width: 112, height: 48, group: 1 },
  { left: 13, top: 16, width: 126, height: 53, group: 3 }, { left: 1, top: 10, width: 100, height: 44, group: 2 },
  { left: 54, top: 11, width: 130, height: 55, group: 1 }, { left: 63, top: 21, width: 113, height: 48, group: 2 },
  { left: 59, top: 4, width: 124, height: 53, group: 3 }, { left: 80, top: 8, width: 122, height: 52, group: 1 },
  { left: 89, top: 21, width: 110, height: 47, group: 2 },
];

const BASE_CLOUD_SHAPES = [
  { left: '0', bottom: '0', width: '100%', height: '60%', borderRadius: '999px' },
  { left: '24%', bottom: '32%', width: '52%', height: '65%', borderRadius: '50%' },
  { left: '52%', bottom: '46%', width: '34%', height: '49%', borderRadius: '50%' },
];
// A storm cloud is a flat base with three puffs (v28 prototype).
const CLOUD_SHAPES = [
  { left: 0, right: 0, bottom: 0, height: '50%', borderRadius: '40px 40px 24px 24px' },
  { left: '10%', bottom: '28%', width: '34%', height: '60%', borderRadius: '50%' },
  { left: '36%', bottom: '34%', width: '40%', height: '66%', borderRadius: '50%' },
  { left: '66%', bottom: '24%', width: '30%', height: '54%', borderRadius: '50%' },
];

// The storm gradient written in px from the layer's centre (the stage's centre), so its colours sit where they do over a box of
// STAGE_W plus the bleed, however wide the layer is. A gradient line at 260 degrees is that box's width and height projected on it.
const STORM_GRADIENT_ANGLE = 260;
const STORM_GRADIENT_LINE_PX = (STAGE_W + 2 * STORM_GRADIENT_SIDE_BLEED) * Math.abs(Math.sin(STORM_GRADIENT_ANGLE * Math.PI / 180))
  + (SKY_H + SKY_ABOVE_STAGE) * Math.abs(Math.cos(STORM_GRADIENT_ANGLE * Math.PI / 180));
const STORM_SKY_GRADIENT = `linear-gradient(${STORM_GRADIENT_ANGLE}deg, ${CROSSING_STORM_SKY_STOPS.map(
  ([color, at]) => `${color} calc(50% + ${((at - 0.5) * STORM_GRADIENT_LINE_PX).toFixed(1)}px)`,
).join(', ')})`;

// The settled storm layer's four clouds, placed in the layer's content box (left/top in %, size in px).
const STORM_LAYER_CLOUDS = [
  { left: '80%', top: '44%', width: 230, height: 78, fill: CROSSING_STORM_CLOUD_FILL.dark, opacity: 1 },
  { left: '60%', top: '30%', width: 260, height: 84, fill: CROSSING_STORM_CLOUD_FILL.mid, opacity: 1 },
  { left: '37%', top: '54%', width: 200, height: 62, fill: CROSSING_STORM_CLOUD_FILL.light, opacity: 0.85 },
  { left: '23%', top: '40%', width: 170, height: 50, fill: CROSSING_STORM_CLOUD_FILL.pale, opacity: 0.55 },
];

// The sun scene's two white clouds, placed in the layer's content box.
const SUN_LAYER_CLOUDS = [
  { left: '60%', top: '46%', width: 180, height: 52, fill: CROSSING_SUN_SCENE_CLOUD_FILL },
  { left: '30%', top: '66%', width: 130, height: 38, fill: CROSSING_SUN_SCENE_CLOUD_FILL_SOFT },
];

// The sliding storm's own three clouds, placed in its full-width box.
const SLIDE_CLOUDS = [
  { left: '8%', top: '24%', width: 260, height: 84, fill: CROSSING_STORM_CLOUD_FILL.mid, opacity: 1 },
  { left: '40%', top: '48%', width: 200, height: 62, fill: CROSSING_STORM_CLOUD_FILL.light, opacity: 0.85 },
  { left: '58%', top: '32%', width: 230, height: 78, fill: CROSSING_STORM_CLOUD_FILL.dark, opacity: 1 },
];

// Opacities and angles go on the element's inline style, so a changing value never mints a new emotion class.
const round = (value: number, places: number) => +value.toFixed(places);

interface BreathSceneArtProps {
  x: number; // round progress 0..1; 1 when no round is armed
  armed: boolean; // a round is running or done: the sea scrolls and the canoe swells
  swellDur: number; // the ramp's current swell length in seconds; drives the wave amplitude, never the animation duration
  canoeRef?: RefObject<HTMLElement | null>; // the element the cue reads the swell's progress from
  backBandRef?: RefObject<HTMLElement | null>; // the bands carry the swell's playbackRate, set from outside
  frontBandRef?: RefObject<HTMLElement | null>;
  intro?: 'rest' | 'sliding' | 'settling' | 'round'; // before the round: the settled sky, then the storm coming in
  opening?: 'sun' | 'storm' | 'done'; // the opening on a first arrival; absent when the sky is simply settled
  sails?: 'set' | 'furled';
  skyKey?: number; // a new value remounts the round's sky layers, so a new round starts from the storm with no fade from clear
  weatherOn?: boolean; // the rain is up (defaults to armed), so it can come in before the round starts
  zoom?: boolean; // the scene zooms to the deck and fades out (Ready for compass)
  idle?: boolean; // the sea and the canoe move at their idle rate while no round is armed
  reducedMotion?: boolean;
}

export default function BreathSceneArt({ x, armed, swellDur, canoeRef, backBandRef, frontBandRef, intro = 'round', opening, sails = 'set', skyKey, weatherOn, zoom = false, idle = false, reducedMotion = false }: BreathSceneArtProps) {
  // While the storm comes in, the base sky is already the round's first moment (hidden under the sun and storm layers and the slide).
  const stormComing = intro === 'sliding' || intro === 'settling';
  const sky = breathSkyAt(stormComing ? 0 : x, intro === 'settling' || (weatherOn ?? armed));
  const { stageScale, raisePx } = useBreathStageScale();
  const amp = breathAmp(armed ? swellDur : 3);
  const cloudInk = sky.cloudInk === 'dark' ? CROSSING_CLOUD_INK_DARK : CROSSING_CLOUD_INK_LIGHT;
  const fade = (seconds: number) => (reducedMotion ? 'none' : `opacity ${seconds}s ease`);
  // Until a round is armed the sky changes without a fade, so a jump to the storm is instant (and hidden under the sun and storm layers).
  const skyFade = (seconds: number) => (armed ? fade(seconds) : 'none');
  const playState = armed || idle ? 'running' : 'paused';
  // The sun and storm layers are drawn until the round's own sky takes over, and go to opacity 0 as the storm settles.
  const skyLayers = intro !== 'round';
  const settling = intro === 'settling';
  const sunOpacity = settling || opening === 'done' || opening === undefined ? 0 : 1;
  const stormOpacity = settling || opening === 'sun' ? 0 : 1;
  const stormTransform = opening === 'sun' ? 'translateX(220px)' : stormComing ? 'translateX(-220px)' : 'none';
  // The settled sky never animates by itself: it only changes with the opening or the storm coming in.
  const layersStill = reducedMotion || (opening === undefined && intro === 'rest');
  const layerFade = settling ? 'opacity 0.1s ease' : 'opacity 2.4s ease';
  const sunTransition = layersStill ? 'none' : layerFade;
  const stormTransition = layersStill ? 'none' : `${layerFade}, transform 2.4s cubic-bezier(.2,.6,.3,1)`;
  // The swell always runs on a 3 s duration; the ramp slows it through playbackRate.
  const swellStyle = { animationDuration: `${SWELL_SEC}s`, animationDelay: '0s', animationPlayState: playState } as const;
  const cloudOpacity = { 1: sky.cloud1Op, 2: sky.cloud2Op, 3: sky.cloud3Op } as const;
  // The keyframes object is interpolated, not .name, so emotion injects its CSS when the class is built.
  const motion = (frames: Keyframes) => (reducedMotion ? 'none' : `${frames}`);

  return (
    <Box
      aria-hidden
      data-motion={reducedMotion ? 'still' : 'moving'}
      sx={{
        position: 'absolute', left: 0, right: 0, top: `-${ABOVE_HOST_PX}px`, bottom: `-${RUNNER_PLATE_CLEARANCE_PX}px`,
        overflow: 'hidden', zIndex: -1, background: CROSSING_BREATH_STAGE_LOWER,
      }}
    >
      {/* Ready for compass zooms the whole scene (stage and rain) about the canoe, then fades it. The origin is the stage's centre,
          written as the same calc as the stage's top, because the root runs from above the host and its own 50% is far off. */}
      <Box
        data-testid="breath-zoom"
        style={{
          transformOrigin: `50% calc(${ABOVE_HOST_PX}px + (100% - ${ABOVE_HOST_PX + RUNNER_PLATE_CLEARANCE_PX}px) / 2 - ${raisePx}px)`,
          transform: zoom ? `scale(${ZOOM_SCALE})` : 'none',
          opacity: zoom ? 0 : 1,
          transition: zoom ? ZOOM_TRANSITION : 'none',
        }}
        sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, pointerEvents: 'none' }}
      >
        <Box
          data-testid="breath-stage"
          style={{ transform: `scale(${round(stageScale, 3)})` }}
          sx={{
            position: 'absolute', width: `${STAGE_W}px`, height: `${STAGE_H}px`, transformOrigin: '50% 50%',
            left: `calc(50% - ${STAGE_W / 2}px)`,
            top: `calc(${ABOVE_HOST_PX}px + (100% - ${ABOVE_HOST_PX + RUNNER_PLATE_CLEARANCE_PX}px) / 2 - ${STAGE_H / 2}px - ${raisePx}px)`,
          }}
        >
          <Box
            data-testid="breath-stage-backdrop"
            sx={{
              position: 'absolute', left: `-${REACH}px`, right: `-${REACH}px`, top: `-${REACH}px`, bottom: `-${REACH}px`,
              background: `linear-gradient(${CROSSING_BREATH_STAGE_UPPER} 0 50%, ${CROSSING_BREATH_STAGE_LOWER} 50% 100%)`,
            }}
          />
          <Box
            data-testid="breath-sky-clear"
            sx={{
              position: 'absolute', left: `-${REACH}px`, right: `-${REACH}px`, top: `-${SKY_OVERSHOOT + REACH}px`, height: `${SKY_H + REACH}px`, zIndex: 0,
              background: `linear-gradient(${CROSSING_BREATH_SKY_TOP} ${REACH}px, ${CROSSING_BREATH_SKY_MID} ${REACH + SKY_H * 0.52}px, ${CROSSING_BREATH_SKY_BOTTOM} ${REACH + SKY_H}px)`,
            }}
          />
          <Box
            key={`overcast-${skyKey}`}
            data-testid="breath-sky-overcast"
            style={{ opacity: round(sky.overcastOp, 3), transition: skyFade(3) }}
            sx={{
              position: 'absolute', left: `-${REACH}px`, right: `-${REACH}px`, top: `-${SKY_OVERSHOOT + REACH}px`, height: `${SKY_H + REACH}px`, zIndex: 1,
              background: `linear-gradient(${CROSSING_OVERCAST_SKY_TOP} ${REACH}px, ${CROSSING_OVERCAST_SKY_BOTTOM} ${REACH + SKY_H}px)`,
            }}
          />
          <Box
            key={`storm-${skyKey}`}
            data-testid="breath-sky-storm"
            style={{ opacity: round(sky.stormOp, 3), transition: skyFade(3) }}
            sx={{
              position: 'absolute', left: `-${REACH}px`, right: `-${REACH}px`, top: `-${SKY_OVERSHOOT + REACH}px`, height: `${SKY_H + REACH}px`, zIndex: 2,
              background: `linear-gradient(${CROSSING_STORM_SKY_TOP} ${REACH}px, ${CROSSING_STORM_SKY_BOTTOM} ${REACH + SKY_H}px)`,
            }}
          />
          {/* No z-index, as in the reference, so the storm and the overcast hide the sun until they fade. */}
          <Box data-testid="breath-sun" sx={{ position: 'absolute', left: '64%', top: '18%', width: '60px', height: '60px', borderRadius: '50%', background: CROSSING_BREATH_SUN }} />

          {skyLayers && (
            <>
              <Box
                data-testid="breath-sun-layer"
                style={{ opacity: sunOpacity, transition: sunTransition }}
                sx={{
                  position: 'absolute', left: `-${REACH}px`, right: `-${REACH}px`, top: `-${SKY_OVERSHOOT + SKY_ABOVE_STAGE}px`, height: `${SKY_H + SKY_ABOVE_STAGE}px`,
                  zIndex: 5, overflow: 'hidden', pointerEvents: 'none',
                  background: `linear-gradient(${CROSSING_SUN_SCENE_GRADIENT_TOP} ${SKY_ABOVE_STAGE}px, ${CROSSING_SUN_SCENE_GRADIENT_MID} ${SKY_ABOVE_STAGE + SKY_H * 0.55}px, ${CROSSING_SUN_SCENE_GRADIENT_BOTTOM} ${SKY_ABOVE_STAGE + SKY_H}px)`,
                }}
              >
                <Box sx={{ position: 'absolute', left: `calc(50% - ${STAGE_W / 2}px)`, bottom: 0, width: `${STAGE_W}px`, height: `${SKY_H}px` }}>
                  <Box
                    data-testid="breath-sun-layer-sun"
                    sx={{
                      position: 'absolute', left: '12%', top: '34%', width: '48px', height: '48px', borderRadius: '50%', background: CROSSING_SUN_SCENE_SUN,
                      boxShadow: `0 0 0 12px ${CROSSING_SUN_SCENE_SUN_HALO}, 0 0 36px 18px ${CROSSING_SUN_SCENE_SUN_GLOW}`,
                    }}
                  />
                  {SUN_LAYER_CLOUDS.map(cloud => (
                    <Box
                      key={cloud.left}
                      data-testid="breath-sun-layer-cloud"
                      sx={{ position: 'absolute', left: cloud.left, top: cloud.top, width: `${cloud.width}px`, height: `${cloud.height}px` }}
                    >
                      {CLOUD_SHAPES.map(shape => (
                        <Box key={shape.left} sx={{ position: 'absolute', ...shape, background: cloud.fill }} />
                      ))}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box
                data-testid="breath-storm-layer"
                style={{ opacity: stormOpacity, transform: stormTransform, transition: stormTransition }}
                sx={{
                  position: 'absolute', left: `-${REACH}px`, right: `-${REACH}px`, top: `-${SKY_OVERSHOOT + SKY_ABOVE_STAGE}px`, height: `${SKY_H + SKY_ABOVE_STAGE}px`,
                  zIndex: 5, overflow: 'hidden', pointerEvents: 'none', background: STORM_SKY_GRADIENT,
                }}
              >
                <Box sx={{ position: 'absolute', left: `calc(50% - ${STAGE_W / 2}px)`, bottom: 0, width: `${STAGE_W}px`, height: `${SKY_H}px` }}>
                  <Box
                    data-testid="breath-storm-layer-glow"
                    sx={{ position: 'absolute', left: '-6%', top: '-10%', width: '46%', height: '120%', background: CROSSING_STORM_SKY_GLOW }}
                  />
                  <Box
                    data-testid="breath-storm-layer-veil"
                    sx={{
                      position: 'absolute', left: '58%', right: '-10%', top: 0, bottom: 0,
                      backgroundImage: `radial-gradient(ellipse 1px 6px at 50% 50%, ${CROSSING_STORM_VEIL_DROP} 96%, transparent 100%)`, backgroundSize: '16px 34px',
                      transform: 'skewX(12deg)',
                      maskImage: 'linear-gradient(270deg, black 0%, black 45%, transparent 100%)',
                      WebkitMaskImage: 'linear-gradient(270deg, black 0%, black 45%, transparent 100%)',
                      animationName: motion(pvRainVeil), animationDuration: '1.5s', animationTimingFunction: 'linear', animationIterationCount: 'infinite',
                    }}
                  />
                  {STORM_LAYER_CLOUDS.map(cloud => (
                    <Box
                      key={cloud.left}
                      data-testid="breath-storm-layer-cloud"
                      style={{ opacity: cloud.opacity }}
                      sx={{ position: 'absolute', left: cloud.left, top: cloud.top, width: `${cloud.width}px`, height: `${cloud.height}px` }}
                    >
                      {CLOUD_SHAPES.map(shape => (
                        <Box key={shape.left} sx={{ position: 'absolute', ...shape, background: cloud.fill }} />
                      ))}
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}

          {stormComing && (
            <Box
              data-testid="breath-storm-slide"
              sx={{
                position: 'absolute', left: 0, right: 0, top: `-${SKY_OVERSHOOT}px`, height: `${SKY_H}px`, zIndex: 6, overflow: 'hidden', pointerEvents: 'none',
                animationName: intro === 'settling' ? motion(pvStormSettle) : 'none', animationDuration: '1.2s', animationTimingFunction: 'ease', animationFillMode: 'forwards',
              }}
            >
              <Box
                data-testid="breath-storm-slide-inner"
                sx={{
                  position: 'absolute', top: 0, right: 0, bottom: 0, left: 0,
                  animationName: intro === 'sliding' ? motion(pvStormSlide) : 'none', animationDuration: '2.2s', animationTimingFunction: 'cubic-bezier(.25,.6,.3,1)', animationFillMode: 'forwards',
                }}
              >
                <Box sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, background: `linear-gradient(${CROSSING_STORM_SKY_TOP}, ${CROSSING_STORM_SKY_BOTTOM})` }} />
                <Box
                  data-testid="breath-slide-veil"
                  sx={{
                    position: 'absolute', left: '-10%', right: '-10%', top: 0, bottom: 0,
                    backgroundImage: `radial-gradient(ellipse 1px 6px at 50% 50%, ${CROSSING_STORM_VEIL_DROP} 96%, transparent 100%)`, backgroundSize: '16px 34px',
                    transform: 'skewX(16deg)',
                    animationName: motion(pvRainVeil), animationDuration: '1.5s', animationTimingFunction: 'linear', animationIterationCount: 'infinite',
                  }}
                />
                {SLIDE_CLOUDS.map(cloud => (
                  <Box
                    key={cloud.left}
                    data-testid="breath-slide-cloud"
                    style={{ opacity: cloud.opacity }}
                    sx={{ position: 'absolute', left: cloud.left, top: cloud.top, width: `${cloud.width}px`, height: `${cloud.height}px` }}
                  >
                    {CLOUD_SHAPES.map(shape => (
                      <Box key={shape.left} sx={{ position: 'absolute', ...shape, background: cloud.fill }} />
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {CLOUDS.map(cloud => (
            <Box
              key={`${cloud.left}-${cloud.top}-${skyKey}`}
              data-testid="breath-cloud"
              data-group={cloud.group}
              style={{ opacity: round(cloudOpacity[cloud.group], 3), transition: skyFade(3) }}
              sx={{ position: 'absolute', left: `${cloud.left}%`, top: `${cloud.top}%`, width: `${cloud.width}px`, height: `${cloud.height}px`, zIndex: 4 }}
            >
              {BASE_CLOUD_SHAPES.map(shape => (
                <Box
                  key={shape.left}
                  data-testid="breath-cloud-shape"
                  style={{ backgroundColor: cloudInk, transition: reducedMotion ? 'none' : 'background 3s ease' }}
                  sx={{ position: 'absolute', ...shape }}
                />
              ))}
            </Box>
          ))}

          <Box data-testid="breath-scene-horizon" sx={{ position: 'absolute', left: `-${REACH}px`, right: `-${REACH}px`, top: `calc(${HORIZON_AT} + 23px)`, bottom: `-${REACH}px`, backgroundColor: CROSSING_SEA_MID }} />
          <Box
            ref={backBandRef}
            data-testid="breath-scene-wave-back"
            style={{ ...swellStyle, transform: `scaleY(${round(amp, 3)})` }}
            sx={{
              position: 'absolute', left: '50%', width: `${WAVE_BAND_W}px`, marginLeft: `-${WAVE_BAND_W / 2}px`, top: `calc(${HORIZON_AT} - 25px)`, height: '60px',
              transformOrigin: '50% 100%', transition: reducedMotion ? 'none' : 'transform 0.6s linear',
              backgroundImage: WAVE_BACK_URL, backgroundSize: '300px 60px', backgroundRepeat: 'repeat-x', backgroundPosition: '0 bottom',
              animationName: motion(rollBack), animationTimingFunction: 'linear', animationIterationCount: 'infinite',
            }}
          />
          <Box data-testid="breath-scene-deep" sx={{ position: 'absolute', left: `-${REACH}px`, right: `-${REACH}px`, top: `calc(${DEEP_AT} + 19px)`, bottom: `-${REACH}px`, backgroundColor: CROSSING_SEA_DEEP }} />
          <Box
            ref={frontBandRef}
            data-testid="breath-scene-wave-front"
            style={{
              animationDuration: `${FRONT_BAND_LOOP_SEC}s`, animationDelay: `${FRONT_BAND_DELAY_SEC}s`, animationPlayState: playState,
              transform: `scaleY(${round(amp, 3)})`,
            }}
            sx={{
              position: 'absolute', left: '50%', width: `${WAVE_BAND_W}px`, marginLeft: `-${WAVE_BAND_W / 2}px`, top: `calc(${DEEP_AT} - 25px)`, height: '56px',
              transformOrigin: '50% 100%', transition: reducedMotion ? 'none' : 'transform 0.6s linear',
              backgroundImage: WAVE_FRONT_URL, backgroundSize: '200px 56px', backgroundRepeat: 'repeat-x', backgroundPosition: '0 bottom',
              animationName: motion(rollFront), animationTimingFunction: 'linear', animationIterationCount: 'infinite',
            }}
          />

          <Box
            data-testid="breath-canoe"
            style={{ top: `calc(${HORIZON_AT} - 14px + ${round(canoeRideHeight(amp), 1)}px)`, transform: `scaleY(${round(amp, 3)})` }}
            sx={{
              position: 'absolute', left: '50%', width: '48px', height: '48px', margin: '-24px 0 0 -24px', zIndex: 6, pointerEvents: 'none',
              transformOrigin: '50% 50%', transition: reducedMotion ? 'none' : 'transform 0.6s linear, top 0.6s linear',
            }}
          >
            <Box
              ref={canoeRef}
              data-testid="breath-canoe-swell"
              style={swellStyle}
              sx={{ display: 'block', width: '48px', height: '48px', animationName: motion(swellCanoe), animationTimingFunction: 'ease-in-out', animationIterationCount: 'infinite' }}
            >
              <Box
                data-testid="breath-canoe-sprite"
                style={{ transform: `scaleY(${round(1 / amp, 3)})` }}
                sx={{ position: 'relative', width: '48px', height: '48px', transformOrigin: '50% 50%' }}
              >
                <WaaCanoe sails={sails} reducedMotion={reducedMotion} />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Full frame and above the stage, so the rain falls over the sky, clouds, sea and canoe. */}
        <Box
          data-testid="breath-rain"
          style={{ opacity: round(sky.rainOp, 3), transition: fade(2.5) }}
          sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, zIndex: 1, overflow: 'hidden', pointerEvents: 'none' }}
        >
          <Box data-testid="breath-rain-slant" sx={{ position: 'absolute', inset: '-240px -160px', transform: 'skewX(-16deg)', transformOrigin: '50% 0' }}>
            {RAIN_LAYERS.map(({ layer, tile, ellipse, alpha: dropAlpha, seconds, frames }) => (
              <Box
                key={layer}
                data-testid={`breath-rain-layer-${layer}`}
                style={{ animationPlayState: sky.rainRunning ? 'running' : 'paused' }}
                sx={{
                  position: 'absolute', top: 0, right: 0, bottom: 0, left: 0,
                  backgroundImage: `radial-gradient(ellipse ${ellipse} at 50% 50%, ${alpha(CROSSING_RAIN_STREAK, dropAlpha)} 96%, transparent 100%)`,
                  backgroundSize: tile,
                  animationName: motion(frames), animationDuration: `${seconds}s`, animationTimingFunction: 'linear', animationIterationCount: 'infinite',
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
